import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Star, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [["all", "All"], ["visible", "Visible"], ["hidden", "Hidden"]];

function ReviewCard({ r, onToggle, onToggleReply }) {
  return (
    <div className={`bg-white border rounded-sm p-5 ${r.hidden ? "border-dashed border-border opacity-70" : "border-border"}`} data-testid={`admin-review-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />)}
            </div>
            {r.hidden && <span className="text-xs px-2 py-0.5 rounded-sm bg-neutral-200 text-neutral-700" data-testid={`admin-review-hidden-badge-${r.id}`}>Hidden from home page</span>}
          </div>
          <p className="text-sm mt-2 text-foreground/90">“{r.comment}”</p>
          <p className="text-xs text-muted-foreground mt-2">
            {r.customer_name} · {r.service_title} by {r.subscriber_name} · {new Date(r.created_at).toLocaleDateString()}
          </p>
          {r.reply && (
            <div className={`mt-3 border-l-2 pl-3 py-1 ${r.reply.hidden ? "border-border opacity-60" : "border-primary/40"}`} data-testid={`admin-review-reply-${r.id}`}>
              <p className="text-xs font-semibold text-foreground/80">Reply from {r.subscriber_name}{r.reply.hidden && <span className="ml-2 font-normal px-1.5 py-0.5 rounded-sm bg-neutral-200 text-neutral-700">Hidden</span>}</p>
              <p className="text-sm text-foreground/80 mt-0.5">{r.reply.text}</p>
              <button onClick={() => onToggleReply(r)} className="text-xs text-primary underline mt-1" data-testid={`admin-reply-toggle-${r.id}`}>
                {r.reply.hidden ? "Unhide reply" : "Hide reply"}
              </button>
            </div>
          )}
        </div>
        <Button size="sm" variant={r.hidden ? "default" : "outline"} className="rounded-sm" onClick={() => onToggle(r)} data-testid={`admin-review-toggle-${r.id}`}>
          {r.hidden ? <><Eye className="w-3.5 h-3.5 mr-1" />Unhide</> : <><EyeOff className="w-3.5 h-3.5 mr-1" />Hide</>}
        </Button>
      </div>
    </div>
  );
}

export const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/admin/reviews").then((r) => setReviews(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    try {
      await api.put(`/admin/reviews/${r.id}/hidden`, { hidden: !r.hidden });
      toast.success(r.hidden ? "Review is visible again." : "Review hidden from the home page.");
      setReviews((list) => list.map((x) => (x.id === r.id ? { ...x, hidden: !r.hidden } : x)));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleReply = async (r) => {
    const hidden = !r.reply.hidden;
    try {
      await api.put(`/admin/reviews/${r.id}/reply-hidden`, { hidden });
      toast.success(hidden ? "Reply hidden." : "Reply is visible again.");
      setReviews((list) => list.map((x) => (x.id === r.id ? { ...x, reply: { ...x.reply, hidden } } : x)));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const shown = reviews.filter((r) => filter === "all" || (filter === "hidden" ? r.hidden : !r.hidden));
  return (
    <div data-testid="admin-reviews-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Hidden reviews never appear on the home page. Customers still see their own review.</p>
        <div className="flex gap-2">
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`admin-reviews-filter-${k}`}
              className={`text-sm px-3 py-1.5 border rounded-sm transition-colors ${filter === k ? "bg-secondary text-white border-secondary" : "border-border hover:border-secondary"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-5 space-y-3">
        {loading ? <p className="text-muted-foreground">Loading…</p>
          : shown.length === 0 ? <p className="text-muted-foreground border border-dashed border-border rounded-sm p-8 text-center" data-testid="admin-reviews-empty">No reviews here yet.</p>
          : shown.map((r) => <ReviewCard key={r.id} r={r} onToggle={toggle} onToggleReply={toggleReply} />)}
      </div>
    </div>
  );
};
